/**
 * Service URL Configuration
 * 
 * Centralized service URL management for all Airclou apps.
 * Automatically detects environment (local/production) and returns appropriate URLs.
 */

export type ServiceName = 'auth' | 'dashboard' | 'playbook' | 'formBuilder' | 'helpdesk' | 'conductor' | 'admin' | 'launcher' | 'scanner';

export interface ServiceUrlConfig {
  local: string;
  production: string;
  localFrontend?: string; // Optional: different port for frontend in local dev
}

/**
 * Service URL mappings
 * All service URLs should be defined here to maintain consistency
 * 
 * Note: Backend services and frontend apps may run on different ports in local dev
 * - Backend APIs typically use 3000-4000 range
 * - Frontend apps (Vite) typically use 5173+ range
 */
const SERVICE_URLS: Record<ServiceName, ServiceUrlConfig> = {
  auth: {
    local: 'http://localhost:4000',
    production: 'https://auth.airclou.com',
    localFrontend: 'http://localhost:4000' // Auth frontend is served by backend
  },
  dashboard: {
    local: 'http://localhost:3003',
    production: 'https://dashboard.airclou.com',
    localFrontend: 'http://localhost:5174' // Dashboard frontend runs on Vite port
  },
  playbook: {
    local: 'http://localhost:3002',
    production: 'https://app.airclou.com/playbook',
    localFrontend: 'http://localhost:5173' // Playbook frontend runs on Vite port
  },
  formBuilder: {
    local: 'http://localhost:3007',
    production: 'https://app.airclou.com/forms',
    localFrontend: 'http://localhost:5178' // FormBuilder frontend runs on Vite port
  },
  helpdesk: {
    local: 'http://localhost:4010',
    production: 'https://app.airclou.com/helpdesk',
    localFrontend: 'http://localhost:5175' // Helpdesk frontend runs on Vite port
  },
  // Conductor is a frontend-only app: it has no backend of its own and talks to
  // the helpdesk API, so `local` points at the helpdesk backend port.
  conductor: {
    local: 'http://localhost:4010',
    production: 'https://app.airclou.com/conductor',
    localFrontend: 'http://localhost:5176' // Conductor frontend runs on Vite port
  },
  admin: {
    local: 'http://localhost:3005',
    production: 'https://admin.airclou.com',
    localFrontend: 'http://localhost:5177' // Admin frontend runs on Vite port
  },
  // The app launcher: the page at the ROOT of app.airclou.com. It has no
  // backend of its own — it calls Auth — so `local` is the same static origin
  // as its frontend. Where a login names no app, this is where it lands.
  launcher: {
    local: 'http://localhost:5179',
    production: 'https://app.airclou.com',
    localFrontend: 'http://localhost:5179'
  },
  // Quality Value Scanner: a personal tool on its own subdomain, limited to
  // allowlisted accounts (apps/scanner/docs/scanner-plan.md §9.9).
  scanner: {
    local: 'http://localhost:3010',
    production: 'https://scanner.airclou.com',
    localFrontend: 'http://localhost:5180'
  }
};

/**
 * Production hostnames - used to detect production environment
 */
const PRODUCTION_HOSTNAMES = [
  'auth.airclou.com',
  'dashboard.airclou.com',
  'app.airclou.com',
  'forms.airclou.com',
  'helpdesk.airclou.com',
  'admin.airclou.com',
  'scanner.airclou.com'
];

/**
 * Check if running in browser environment
 */
export function isBrowserEnvironment(): boolean {
  return typeof window !== 'undefined' && typeof window.location !== 'undefined';
}

/**
 * Check if current hostname is localhost
 */
export function isLocalhost(): boolean {
  if (!isBrowserEnvironment()) return false;
  const hostname = window.location.hostname;
  const port = window.location.port;
  // Common local hostnames
  const localHostnames = new Set(['localhost', '127.0.0.1', '0.0.0.0']);
  // Consider *.local and *.localhost domains as local dev too
  const isLocalLikeHostname =
    localHostnames.has(hostname) ||
    hostname.endsWith('.local') ||
    hostname.endsWith('.localhost');
  // Known dev ports across apps (Vite + backends)
  const devPorts = new Set(['5173', '5174', '5175', '5176', '5177', '5178', '3002', '3003', '3004', '3005', '4000']);
  const isDevPort = port ? devPorts.has(port) : false;
  return isLocalLikeHostname || isDevPort;
}

/**
 * Check if current hostname is a production hostname
 */
export function isProductionHostname(): boolean {
  if (!isBrowserEnvironment()) return false;
  const hostname = window.location.hostname;
  return PRODUCTION_HOSTNAMES.includes(hostname);
}

/**
 * Get the current environment
 */
export function getEnvironment(): 'local' | 'production' {
  if (!isBrowserEnvironment()) {
    // SSR/build time: check NODE_ENV
    // Also honor FORCE_LOCAL_ENVIRONMENT for testing
    if (typeof process !== 'undefined') {
      if (process.env?.FORCE_LOCAL_ENVIRONMENT === 'true') {
        return 'local';
      }
      if (process.env?.NODE_ENV === 'development' || process.env?.NODE_ENV === 'test') {
        return 'local';
      }
    }
    return 'production';
  }
  
  // Browser runtime: check hostname
  return isLocalhost() ? 'local' : 'production';
}

/**
 * Get base URL for a service based on current environment
 * 
 * @param service - The service name
 * @param options - Optional configuration
 * @returns The base URL for the service
 * 
 * @example
 * ```ts
 * const authUrl = getServiceUrl('auth');
 * // Returns 'http://localhost:4000' in local, 'https://auth.airclou.com' in production
 * 
 * const dashboardApi = getServiceUrl('dashboard', { includeApi: true });
 * // Returns 'http://localhost:3003/api' in local, 'https://dashboard.airclou.com/api' in production
 * 
 * const playbookFrontend = getServiceUrl('playbook', { useFrontend: true });
 * // Returns 'http://localhost:5173' in local, 'https://app.airclou.com/playbook' in production
 * ```
 */
export function getServiceUrl(
  service: ServiceName,
  options?: {
    includeApi?: boolean;
    useFrontend?: boolean;
    forceEnvironment?: 'local' | 'production';
  }
): string {
  const config = SERVICE_URLS[service];
  if (!config) {
    throw new Error(`Unknown service: ${service}`);
  }
  
  const env = options?.forceEnvironment || getEnvironment();
  
  // Determine base URL
  let baseUrl: string;
  if (env === 'local' && options?.useFrontend && config.localFrontend) {
    // Use frontend port for local development
    baseUrl = config.localFrontend;
  } else {
    // Use standard URL (backend or production)
    baseUrl = config[env];
  }
  
  if (options?.includeApi) {
    return `${baseUrl}/api`;
  }
  
  return baseUrl;
}

/**
 * Get API URL for a service (convenience function)
 * Equivalent to getServiceUrl(service, { includeApi: true })
 */
export function getServiceApiUrl(service: ServiceName): string {
  return getServiceUrl(service, { includeApi: true });
}

/**
 * Get frontend URL for a service (convenience function)
 * In production, returns the same as getServiceUrl()
 * In local dev, returns the frontend port (Vite dev server)
 * 
 * @example
 * ```ts
 * const playbookFrontend = getServiceFrontendUrl('playbook');
 * // Local: 'http://localhost:5173' | Production: 'https://app.airclou.com/playbook'
 * ```
 */
export function getServiceFrontendUrl(service: ServiceName): string {
  return getServiceUrl(service, { useFrontend: true });
}

/**
 * Get all service URLs for the current environment
 * Useful for CORS configuration, CSP headers, etc.
 */
export function getAllServiceUrls(options?: {
  includeApi?: boolean;
  useFrontend?: boolean;
  forceEnvironment?: 'local' | 'production';
}): Record<ServiceName, string> {
  // Derived from the map rather than a hand-kept list: this feeds the CORS and
  // redirect allowlists, and a service missing here is a service whose origin
  // those lists silently omit.
  const services = Object.keys(SERVICE_URLS) as ServiceName[];

  return services.reduce((acc, service) => {
    acc[service] = getServiceUrl(service, options);
    return acc;
  }, {} as Record<ServiceName, string>);
}

